import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import find from 'lodash/find';
import keyBy from 'lodash/keyBy';
import throttle from 'lodash/throttle';

import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { isSmScreen, isMdScreen } from '@client/helpers/screens';
import { EVENT_ENTITY_TYPE } from '@client/config/constants';
import { buildUrlForEntity } from '../../shared/Helpers';
import { getImagesLoaded } from '../../../redux/modules/mylounge-legacy/selectors';
import { track } from '@client/helpers';
import { EntityType } from '@shared/types/mylounge';

import BannerSlider from './banner-slider-legacy';
import BannerContent from './banner-content-legacy';
import Tabs from './Tabs/Tabs';
import {
    SM_TABS_OFFSET,
    MD_TABS_OFFSET,
    LG_TABS_OFFSET,
    TAG_NAME_TRACK_MAPPING,
    SCROLL_TRACK_TOP_OFFSET,
    TAB_SWIPE_TRACK_EVENT_NAME,
    TAB_CLICK_TRACK_EVENT_NAME,
    SCROLL_TRACK_EVENT_NAME,
} from './constants';
import {
    OutterWrapper,
    Wrapper,
    InnerWrapper,
    TabsWrapper,
} from './Banner.styles';

export class Banner extends PureComponent {
    static propTypes = {
        tabs: PropTypes.array.isRequired,
        selectedFilter: PropTypes.string.isRequired,
        imagesLoaded: PropTypes.bool.isRequired,
        clientWidth: PropTypes.number.isRequired,
        showXmas: PropTypes.bool,
    };

    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);

        const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;

        this.state = {
            lastIndex: null,
            activeIndex: this.findActiveTab(props.tabs, props.selectedFilter),
            tabImages: this.updateTabImagesMap(props.tabs, false),
            lastScrollY: scrollY,
            floating: false,
            isTabsVisible: false,
            scrollTracked: false,
        };

        this.handleScrollEvent = throttle(this.handleScrollEvent, 100);
    }

    render() {
        const {
            tabs
        } = this.props;

        const {
            lastIndex,
            activeIndex,
            tabImages,
            isTabsVisible,
            floating,
        } = this.state;

        if (!tabs[activeIndex]) {
            return null;
        }

        const { highlight: { ctaText, ctaTarget } } = tabs[activeIndex];

        return (
            <OutterWrapper>
                <Wrapper>
                    <BannerSlider
                        tabs={tabs}
                        activeIndex={activeIndex}
                        lastIndex={lastIndex}
                        onBannerSwipe={this.handleTabSwipe}
                        tabImages={tabImages}
                        onImageClick={this.navigateToCtaTarget}
                    />
                    <InnerWrapper>
                        <BannerContent text={ctaText} onButtonClick={this.navigateToCtaTarget} ctaTarget={ctaTarget} />
                    </InnerWrapper>
                </Wrapper>
                <TabsWrapper
                    floating={floating}
                    floatingHidden={!isTabsVisible && floating}
                >
                    <Tabs
                        ref={this.tabsRef}
                        tabs={tabs}
                        onTabClick={this.handleTabClick}
                        activeIndex={activeIndex}
                        lastIndex={lastIndex}
                    />
                </TabsWrapper>
            </OutterWrapper>
        );
    }

    componentDidMount() {
        window.addEventListener('scroll', this.handleScrollEvent);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.imagesLoaded !== this.props.imagesLoaded && this.props.imagesLoaded === true) {
            this.loadAllImages();
        }
        if (prevProps.selectedFilter !== this.props.selectedFilter) {
            this.setActiveTabIndex();
        }
    }

    componentWillUnmount = () => {
        this.clearTimeout();
        window.removeEventListener('scroll', this.handleScrollEvent);
    }

    getTopBreakpointOffset = () => {
        const { clientWidth } = this.props;

        if (isSmScreen(clientWidth)) {
            return SM_TABS_OFFSET;
        }
        else if (isMdScreen(clientWidth)) {
            return MD_TABS_OFFSET;
        }

        return LG_TABS_OFFSET;
    }

    updateTabsPosition = () => {
        const { lastScrollY } = this.state;
        const { clientWidth } = this.props;

        const isSmOrLess = isSmScreen(clientWidth);
        const scrollDown = window.scrollY > lastScrollY;
        const tabsOffset = scrollDown && isSmOrLess ? 50 : 0;

        const topOffset = this.getTopBreakpointOffset() + tabsOffset;

        this.setState({
            floating: window.scrollY > topOffset,
            isTabsVisible: !isSmOrLess || !scrollDown,
            lastScrollY: window.scrollY,
        });
    }

    handleScrollEvent = () => {
        const { scrollTracked } = this.state;
        const { clientWidth } = this.props;

        if (!scrollTracked && (!isSmScreen(clientWidth) || window.scrollY > SCROLL_TRACK_TOP_OFFSET)) {
            this.trackScroll();
        }

        this.updateTabsPosition();
    }

    setActiveTabIndex() {
        const { tabs, selectedFilter } = this.props;
        const { activeIndex } = this.state;
        const newActiveIndex = this.findActiveTab(tabs, selectedFilter);
        if (newActiveIndex !== activeIndex) {
            this.setState({
                activeIndex: newActiveIndex > -1 ? newActiveIndex : 0,
                lastIndex: activeIndex,
                tabImages: {
                    ...this.state.tabImages,
                    [newActiveIndex]: { index: newActiveIndex, loaded: true }
                }
            });
        }
    }

    updateTabImagesMap = (tabs, loaded, active) => {
        return keyBy(tabs.map((tab, index) => ({ index, loaded: index === active || loaded })), 'index');
    }

    loadAllImages = () => {
        const { tabs } = this.props;
        this.setState({
            tabImages: { ...this.updateTabImagesMap(tabs, true) }
        });
    }

    findActiveTab = (tabs, selectedFilter) => {
        const tab = find(tabs, { tag: selectedFilter || 'all' });

        return tabs.indexOf(tab);
    }

    trackScroll = () => {
        this.setState({ scrollTracked: true });

        const { tabs } = this.props;
        const { activeIndex } = this.state;
        const { tag } = tabs[activeIndex] || {};

        track(SCROLL_TRACK_EVENT_NAME, { campaign_level_2: TAG_NAME_TRACK_MAPPING[tag] || tag });
    }

    trackTabChange = (eventName, newActiveIndex) => {
        const { tabs } = this.props;
        const { tag } = tabs[newActiveIndex];

        track(eventName, { campaign_level_2: TAG_NAME_TRACK_MAPPING[tag] || tag });
    }

    isSameTab = newActiveIndex => newActiveIndex === this.state.activeIndex;

    handleTabClick = (newActiveIndex, timeout) => {
        if (this.isSameTab(newActiveIndex)) {
            return;
        }

        this.trackTabChange(TAB_CLICK_TRACK_EVENT_NAME, newActiveIndex);
        this.changeActiveTab(newActiveIndex, timeout);
    }

    handleTabSwipe = (newActiveIndex, timeout) => {
        if (this.isSameTab(newActiveIndex)) {
            return;
        }

        this.trackTabChange(TAB_SWIPE_TRACK_EVENT_NAME, newActiveIndex);
        this.changeActiveTab(newActiveIndex, timeout);
    }

    changeActiveTab = (newActiveIndex, timeout = 200) => {

        this.setState({
            activeIndex: newActiveIndex,
            lastIndex: this.state.activeIndex,
            tabImages: {
                ...this.state.tabImages,
                [newActiveIndex]: { index: newActiveIndex, loaded: true }
            }
        });

        const { tabs } = this.props;
        const { tag } = tabs[newActiveIndex];

        this.tabChangeTimeout = setTimeout(() => {
            this.context.router.push(`/event/${tag}${window.location.search}`);

            this.setState({ scrollTracked: false });
        }, timeout);
    }

    navigateToCtaTarget = () => {
        const { activeIndex } = this.state;
        const { tabs } = this.props;
        const { highlight: { ctaTarget } } = tabs[activeIndex];

        if (!ctaTarget) {
            return;
        }

        const { entityType, entityId } = ctaTarget;

        this.trackBannerClick(ctaTarget);
        const url = buildUrlForEntity(ctaTarget);

        if (!url) return;

        // state transition for tabs change from thb cta
        if (entityId && entityType === EntityType.CATEGORY_TAB) {
            this.context.router.push(url);

            return;
        }

        // page changes to catalog
        window.location.href = url;
    }

    trackBannerClick = (ctaTarget) => {
        const campaignId = (ctaTarget.entityType === EVENT_ENTITY_TYPE) ?
            ctaTarget.entityId : this.getCampaignIdFromUrl(ctaTarget.url);

        track('co-discover-button', {
            brandClicked: campaignId
        });
    }

    getCampaignIdFromUrl = (url) => url && url.split('=')[1]

    clearTimeout = () => {
        if (this.tabChangeTimeout) {
            clearTimeout(this.tabChangeTimeout);
            this.tabChangeTimeout = null;
        }
    }
}

const mapStateToProps = (state) => ({
    imagesLoaded: getImagesLoaded(state),
    clientWidth: getClientDeviceWidth(state)
});

export default connect(mapStateToProps)(Banner);
